import { type DependencyList, useEffect } from "react";
import { useStore } from "react-redux";
import type { Store, Action, UnknownAction } from "redux";
import { useCommonSelector } from "../redux/commonStore";
import type { CommonSettingsType } from "../settings/fetchCommonSettings";
import { getAPI } from "../settings/utils/commonSettingUtils";
import type { DataEndpointBodyType, DataEndpointReturnType } from "../server/v2/endpoints/DataEndpoint";
import Debug from "../Debug";
import type { Fetch, PostFetchFnType, FetchDispatcherType, GetFetchesFnType } from "../utils/registryTypes";
import type { ComponentType } from "../../shared-logic/server/components";
import { type CommonPostFetchStore, commonPostFetch } from "../server/v2/utils/commonPostFetch";
import type { QueryType } from "../server/types";
import { addErrorLog, addRequestLog } from "../redux/actions/UtilActions";
import type { ErrorLogType } from "../types/CommonTypes";
import type { RequestLogType } from "../utils/backendFetch";

type StateBaseType = { commonSettings: CommonSettingsType };

// TODO We could consider wrapping this with a registry for easier and more consistent usage.
export const executeFrontendDataFetch = async <S extends Store, F extends Fetch>(
    store: S,
    getFetches: GetFetchesFnType,
    fetchDispatcher: FetchDispatcherType<F>,
    commonSettings: CommonSettingsType,
    component: ComponentType, // Although this is available in commonSettings, we need this value manually for cases where another component is integrated.
    propagatedParams: (keyof QueryType)[], // Set of query/body params which will be propagated to the data endpoint.
    postFetch?: PostFetchFnType<S>,
): Promise<void> => {
    const { dispatch } = store;

    let count = 0;
    const executeFetch = async (): Promise<void> => {
        count++;
        // Get most recent state, calculate fetches.
        // ⚠️ Do not use state like this in other hooks or components, this is specifically done for data fetching ⚠️
        const state = store.getState();
        const fetches = getFetches(state);

        if (count >= 10) {
            // Infinite loop failsafe.
            Debug.error(`Data fetch loop detected: ${fetches[0]?.fetchType}`);
            return;
        }

        if (fetches.length) {
            // Fetches available, go to server and fetch.
            // Gather additional params to be added to the request if required.
            const reqBody = commonSettings.body as Record<string, unknown>;
            const reqQuery = commonSettings.query as Record<string, unknown>;

            const propagatedQueries: Record<string, unknown> = {};
            propagatedParams.forEach((key) => {
                if (reqBody[key] !== undefined) propagatedQueries[key] = reqBody[key];
                if (reqQuery[key] !== undefined) propagatedQueries[key] = reqQuery[key];
            });

            const url = getAPI(commonSettings, "data", "", propagatedQueries);
            const body: DataEndpointBodyType = { component, fetches };
            const result = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const parsedResult: DataEndpointReturnType = await result.json();

            // In addition to Fetch add the optional reqLog and errorLog arrays which are commonly used.
            // At some point we might be able to add these to Fetch type by default but there is currently too many variances across OR.
            type ResultType = Required<F> & {
                result?: { [extraProps: string]: unknown; reqLogs?: RequestLogType[]; errorLogs?: ErrorLogType[] };
            };
            // Fetches received, set them in state.
            parsedResult.fetches.forEach((fetch: ResultType) => {
                fetchDispatcher(fetch, dispatch);

                if (fetch?.result?.reqLogs?.length) store.dispatch(addRequestLog(fetch.result?.reqLogs));
                if (fetch?.result?.errorLogs?.length) store.dispatch(addErrorLog(fetch.result?.errorLogs));
            });

            // Start the next loop.
            await executeFetch();
        } else {
            if (postFetch) {
                // Fetch loop is done, trigger postFetch if available.
                await postFetch(store);
            }
            commonPostFetch(store as CommonPostFetchStore);
        }
    };
    try {
        await executeFetch();
    } catch (ex) {
        Debug.error("Failed to execute fetch loop", ex);
    }
};

/**
 * This hook can be used to fetch data usually retrieved during SSR.
 * The fetch fns used here should be the exact same ones used in the component registry.
 * Current setup is based on ORv2, v1 is not supported for now.
 *
 * @param getFetches - Component fetch function
 * @param fetchDispatcher - Component Fetch dispatcher function
 * @param deps - Hook dependencies, when the dep changes the fetchloop will be ran again. use sparingly.
 * @param postFetch - Optional component postFetch function
 */
const useDataFetcher = <S extends StateBaseType, F extends Fetch, A extends Action = UnknownAction>(
    getFetches: GetFetchesFnType,
    fetchDispatcher: FetchDispatcherType<F>,
    component: ComponentType,
    deps: DependencyList = [],
    propagatedParams: (keyof QueryType)[],
    postFetch?: PostFetchFnType<Store<S, A>>,
): void => {
    const store = useStore<S, A>();
    const commonSettings = useCommonSelector((state) => state.commonSettings);

    useEffect(() => {
        executeFrontendDataFetch(
            store,
            getFetches,
            fetchDispatcher,
            commonSettings,
            component,
            propagatedParams,
            postFetch,
        );
    }, deps);
};

export default useDataFetcher;
